import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
};

// Overview
export const fetchOverview = async (range = '30d') => {
  const { data } = await axios.get(
    `${API_BASE}/api/seller/analytics/overview?range=${range}`,
    getAuthHeaders()
  );
  
  // Transform backend response structure to match frontend expectations
  // Backend returns { tiles: {...}, ads: {...}, ... }
  // Frontend expects flat structure with ad_ prefix for ad metrics
  const tiles = data.tiles || {};
  const ads = data.ads || {};
  
  return {
    // Flat structure from tiles
    views: tiles.views || 0,
    views_delta: 0, // TODO: Calculate delta from comparison window
    clicks: tiles.clicks || 0,
    clicks_delta: 0,
    ctr: tiles.ctr || 0,
    ctr_delta: 0,
    messages_sent: tiles.messages_sent || 0,
    messages_sent_delta: 0,
    orders_created: tiles.orders || 0,
    orders_created_delta: 0,
    revenue_cents: tiles.gmv_cents || 0,
    revenue_delta: 0,
    view_to_order_rate: tiles.conv_rate || 0,
    view_to_order_rate_delta: 0,
    avg_order_value_cents: tiles.aov_cents || 0,
    orders_completed: tiles.orders || 0,
    orders_completed_delta: 0,
    
    // Ad metrics with ad_ prefix
    has_ad_data: (ads.impressions_top + ads.impressions_mid) > 0,
    ad_impressions: (ads.impressions_top || 0) + (ads.impressions_mid || 0),
    ad_impressions_delta: 0,
    ad_clicks: (ads.clicks_top || 0) + (ads.clicks_mid || 0),
    ad_clicks_delta: 0,
    ad_ctr: ads.ctr || 0,
    ad_ctr_delta: 0,
    ad_spend_cents: ads.spend_cents || 0,
    ad_spend_delta: 0,
    ad_cpc_cents: ads.avg_cpc_cents || 0,
    ad_orders: ads.orders_attrib || 0,
    ad_orders_delta: 0,
    ad_roas: ads.roas || 0,
    ad_cpa_cents: ads.cpa_cents || 0,
  };
};

// Listings Table
export const fetchListings = async ({ range = '30d', sort = 'views', dir = 'desc', page = 1, pageSize = 25, search = '' }) => {
  const params = new URLSearchParams({
    range,
    sort,
    dir,
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  if (search) params.append('search', search);
  
  const { data } = await axios.get(
    `${API_BASE}/api/seller/analytics/listings?${params}`,
    getAuthHeaders()
  );
  
  // Transform backend response (rows → listings)
  return {
    ...data,
    listings: data.rows || [],
    total_pages: Math.ceil((data.total || 0) / (data.page_size || pageSize))
  };
};

// Listing Funnel
export const fetchListingFunnel = async (listingId, range = '30d') => {
  const { data } = await axios.get(
    `${API_BASE}/api/seller/analytics/listings/${listingId}/funnel?range=${range}`,
    getAuthHeaders()
  );
  return data;
};

// Ads Advanced
export const fetchAdsAdvanced = async (range = '30d') => {
  const { data } = await axios.get(
    `${API_BASE}/api/seller/analytics/ads/advanced?range=${range}`,
    getAuthHeaders()
  );
  return data;
};

// Benchmarks
export const fetchBenchmarks = async ({ range = '30d', categoryId = null, tier = null, metro = null }) => {
  const params = new URLSearchParams({ range });
  if (categoryId) params.append('categoryId', categoryId);
  if (tier) params.append('tier', tier);
  if (metro) params.append('metro', metro);
  
  const { data } = await axios.get(
    `${API_BASE}/api/seller/analytics/benchmarks?${params}`,
    getAuthHeaders()
  );
  return data;
};

// Recommendations
export const fetchRecommendations = async ({ range = '30d', categoryId = null, metro = null }) => {
  const params = new URLSearchParams({ range });
  if (categoryId) params.append('categoryId', categoryId);
  if (metro) params.append('metro', metro);
  
  const { data } = await axios.get(
    `${API_BASE}/api/seller/analytics/recommendations?${params}`,
    getAuthHeaders()
  );
  return data;
};

// Nudges/Alerts
export const fetchNudges = async (range = '30d') => {
  const { data } = await axios.get(
    `${API_BASE}/api/seller/analytics/nudges?range=${range}`,
    getAuthHeaders()
  );
  return data;
};

// Dismiss Nudge
export const dismissNudge = async ({ key, durationDays = 30 }) => {
  const { data } = await axios.post(
    `${API_BASE}/api/seller/analytics/nudges/dismiss`,
    { key, duration_days: durationDays },
    getAuthHeaders()
  );
  return data;
};

// CSV Export URL generator
export const getExportUrl = (type, range = '30d') => {
  const token = localStorage.getItem('token');
  return `${API_BASE}/api/seller/analytics/export.csv?range=${range}&type=${type}&token=${token}`;
};
